import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";

import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function SubscriptionConfirmation() {
  const { style } = useStyle();

  return (
    <>
      <Seo
        title="Subscription Confirmed | Parish Audio"
        description="Your Parish Audio subscription is active. Download your plugins from your My Gear page."
        canonical="https://parishaudio.com/subscription-confirmation"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
          display: "flex",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 920,
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                position: "relative",
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 3, md: 4 },
                borderBottom: `1px solid ${style.color2}`,
              }}
            >
              <Stack spacing={1.25} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 74,
                    height: 74,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(88,166,124,0.16)",
                    border: "1px solid rgba(88,166,124,0.28)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
                  }}
                >
                  <CheckCircleRoundedIcon
                    sx={{
                      fontSize: 44,
                      color: style.accentGreen,
                    }}
                  />
                </Box>

                <Chip
                  label="Subscription Active"
                  sx={{
                    borderRadius: "5px",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: style.accentGreen,
                  }}
                />

                <Typography
                  sx={{
                    color: style.color4,
                    fontSize: { xs: "1.9rem", md: "2.6rem" },
                    fontWeight: 800,
                    lineHeight: 1.02,
                    letterSpacing: "-0.03em",
                    maxWidth: 700,
                  }}
                >
                  Thank you for subscribing
                </Typography>

                <Typography
                  sx={{
                    color: style.color3,
                    fontSize: { xs: "1rem", md: "1.05rem" },
                    lineHeight: 1.7,
                    maxWidth: 620,
                  }}
                >
                  Your subscription is active, and your included products are now available on your{" "}
                  <Box
                    component={Link}
                    to="/my-gear"
                    sx={{
                      color: style.color4,
                      fontWeight: 700,
                      textDecoration: "none",
                    }}
                  >
                    My Gear
                  </Box>{" "}
                  page.
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2, md: 2.5 },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  borderRadius: "14px",
                  backgroundColor: style.darkPanel,
                  border: "1px solid rgba(255,255,255,0.08)",
                  textAlign: "center",
                }}
              >
                <Stack spacing={1}>
                  <Typography
                    sx={{
                      color: style.color4,
                      fontWeight: 800,
                      fontSize: "1.05rem",
                    }}
                  >
                    Your downloads are ready!
                  </Typography>
                </Stack>
              </Paper>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                justifyContent="center"
                sx={{ mt: 2.25 }}
              >
                <Button
                  component={Link}
                  to="/my-gear"
                  startIcon={<DownloadRoundedIcon />}
                  sx={{
                    borderRadius: "5px",
                    textTransform: "none",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: style.accentGreen,
                    minWidth: { xs: "100%", sm: 170 },
                    "&:hover": {
                      backgroundColor: style.accentGreen,
                      filter: "brightness(1.05)",
                    },
                  }}
                >
                  Go to My Gear
                </Button>

                <Button
                  component={Link}
                  to="/"
                  startIcon={<HomeRoundedIcon />}
                  sx={{
                    borderRadius: "5px",
                    textTransform: "none",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    minWidth: { xs: "100%", sm: 150 },
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.10)",
                    },
                  }}
                >
                  Return Home
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}